import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { API_URL as API } from '../lib/api';

const TaskContext = createContext(null);

export function TaskProvider({ children }) {
  const [tasks, setTasks] = useState([]);
  const [notifCounts, setNotifCounts] = useState({ completed: {}, running: {} });
  const [newlyCompleted, setNewlyCompleted] = useState([]);
  const prevTasksRef = useRef({});

  const fetchTasks = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/tasks`, { withCredentials: true });
      // Detect newly completed tasks
      const prev = prevTasksRef.current;
      const justDone = [];
      data.forEach(t => {
        const old = prev[t.id];
        if ((t.status === 'done' || t.status === 'error') && old && old.status === 'running') {
          justDone.push(t);
        }
      });
      if (justDone.length > 0) {
        setNewlyCompleted(nc => [...nc, ...justDone]);
      }
      // Update ref
      const map = {};
      data.forEach(t => { map[t.id] = t; });
      prevTasksRef.current = map;
      setTasks(data);
    } catch {}
  }, []);

  const fetchCounts = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/tasks/notifications/counts`, { withCredentials: true });
      setNotifCounts(data);
    } catch {}
  }, []);

  // Poll every 10s for active tasks, 30s otherwise
  useEffect(() => {
    fetchTasks();
    fetchCounts();
    const hasRunning = tasks.some(t => t.status === 'running');
    const interval = setInterval(() => {
      fetchTasks();
      fetchCounts();
    }, hasRunning ? 10000 : 30000);
    return () => clearInterval(interval);
  }, [tasks.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const dismissTask = useCallback(async (taskId) => {
    try {
      await axios.put(`${API}/api/tasks/${taskId}/dismiss`, {}, { withCredentials: true });
      setNewlyCompleted(nc => nc.filter(t => t.id !== taskId));
      fetchCounts();
    } catch {}
  }, [fetchCounts]);

  const dismissBlock = useCallback(async (block) => {
    try {
      await axios.put(`${API}/api/tasks/dismiss-block/${block}`, {}, { withCredentials: true });
      fetchCounts();
    } catch {}
  }, [fetchCounts]);

  const dismissNotification = useCallback((taskId) => {
    setNewlyCompleted(nc => nc.filter(t => t.id !== taskId));
  }, []);

  const getBlockCount = useCallback((block) => {
    return (notifCounts.completed?.[block] || 0);
  }, [notifCounts]);

  const getRunningCount = useCallback((block) => {
    return (notifCounts.running?.[block] || 0);
  }, [notifCounts]);

  return (
    <TaskContext.Provider value={{
      tasks,
      notifCounts,
      newlyCompleted,
      fetchTasks,
      fetchCounts,
      dismissTask,
      dismissBlock,
      dismissNotification,
      getBlockCount,
      getRunningCount,
    }}>
      {children}
    </TaskContext.Provider>
  );
}

export const useTaskContext = () => useContext(TaskContext);
